import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { BellOff, Mail, Cake, Gift, Megaphone, AlertTriangle, Users, TrendingDown, Loader2 } from "lucide-react";
import api from "../../lib/api";

/**
 * PreferencesStatsCard — panel de métricas agregadas sobre:
 *  - qué % de clientes se han dado de baja (y por qué motivo)
 *  - qué % ha silenciado cada tipo de comunicación (campañas / cumpleaños / recompensas)
 *
 * Props: restaurantId
 */

const SOURCE_LABELS = {
  email_link: { label: "Desde email", color: "text-foreground dark:text-slate-300" },
  complained: { label: "Marcaron como spam", color: "text-rose-700 dark:text-rose-400" },
  bounced:    { label: "Email rebotado", color: "text-amber-700 dark:text-amber-400" },
  test:       { label: "Datos de prueba", color: "text-muted-foreground" },
  unknown:    { label: "Desconocido", color: "text-muted-foreground" },
};

function BigMetric({ label, value, sub, Icon, tone = "violet" }) {
  const toneClasses = {
    violet:  { bg: "bg-violet-100 dark:bg-violet-900/30",   fg: "text-violet-700 dark:text-violet-300" },
    emerald: { bg: "bg-emerald-100 dark:bg-emerald-900/30", fg: "text-emerald-700 dark:text-emerald-300" },
    rose:    { bg: "bg-rose-100 dark:bg-rose-900/30",       fg: "text-rose-700 dark:text-rose-300" },
    amber:   { bg: "bg-amber-100 dark:bg-amber-900/30",     fg: "text-amber-700 dark:text-amber-300" },
  }[tone] || { bg: "bg-muted", fg: "text-foreground" };

  return (
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl ${toneClasses.bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${toneClasses.fg}`} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold leading-tight">{value}</p>
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function OptOutRow({ label, count, pct, Icon }) {
  // 0..100 normalized width; over 25% we visually highlight it
  const width = Math.min(Math.max(pct, 0), 100);
  const highlight = pct >= 25;
  return (
    <div className="flex items-center gap-3 py-2.5" data-testid={`optout-row-${label}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
        highlight ? "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400"
                  : "bg-muted text-muted-foreground"
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <p className="text-sm font-medium truncate">{label}</p>
          <p className="text-xs tabular-nums">
            <span className={`font-bold ${highlight ? "text-rose-700" : ""}`}>{pct}%</span>
            <span className="text-muted-foreground ml-1">({count})</span>
          </p>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all rounded-full ${highlight ? "bg-rose-500" : "bg-violet-500"}`}
            style={{ width: `${width}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function PreferencesStatsCard({ restaurantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.getLoyaltyPreferencesStats(restaurantId);
      setData(r);
    } catch {
      // Silent — stats are an optional widget; restaurant context errors are
      // surfaced by the parent customers page once.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card className="border-none shadow-soft">
        <CardContent className="p-6 flex items-center justify-center min-h-[140px]">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.with_email === 0) {
    return null; // nothing to show — hide the widget silently for restaurants without email data
  }

  return (
    <Card className="border-none shadow-soft" data-testid="preferences-stats-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-5">
          <BellOff className="w-4 h-4 text-muted-foreground" />
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Preferencias de comunicación</p>
        </div>

        {/* Top row — 3 KPIs */}
        <div className="grid sm:grid-cols-3 gap-5 pb-5 border-b border-border">
          <BigMetric
            label="Alcanzables"
            value={data.deliverable}
            sub={`${data.with_email} con email`}
            Icon={Mail}
            tone="emerald"
          />
          <BigMetric
            label="Bajas totales"
            value={data.unsubscribed}
            sub={`${data.unsub_pct}% de clientes con email`}
            Icon={BellOff}
            tone={data.unsub_pct >= 5 ? "rose" : "violet"}
          />
          <BigMetric
            label="Bajas últimos 30d"
            value={data.unsub_last_30d}
            sub={data.unsub_last_7d > 0 ? `${data.unsub_last_7d} en últimos 7d` : "Sin bajas esta semana"}
            Icon={TrendingDown}
            tone={data.unsub_last_7d >= 3 ? "rose" : "amber"}
          />
        </div>

        {/* Opt-out breakdown */}
        <div className="pt-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Silenciado por tipo</p>
          <OptOutRow label="Campañas y novedades" count={data.opt_out.campaigns.count}   pct={data.opt_out.campaigns.pct}   Icon={Megaphone} />
          <OptOutRow label="Cumpleaños"            count={data.opt_out.birthday.count}    pct={data.opt_out.birthday.pct}    Icon={Cake} />
          <OptOutRow label="Nuevas recompensas"    count={data.opt_out.new_rewards.count} pct={data.opt_out.new_rewards.pct} Icon={Gift} />
        </div>

        {/* Sources breakdown */}
        {data.sources?.length > 0 && (
          <div className="pt-4 mt-4 border-t border-border">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Motivos de baja</p>
            <div className="flex flex-wrap gap-2">
              {data.sources.map((s) => {
                const cfg = SOURCE_LABELS[s.source] || SOURCE_LABELS.unknown;
                return (
                  <div key={s.source} className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs" data-testid={`source-${s.source}`}>
                    <span className={cfg.color + " font-medium"}>{cfg.label}</span>
                    <span className="text-muted-foreground">· {s.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actionable tip if high churn */}
        {data.unsub_pct >= 5 && (
          <div className="mt-4 p-3 rounded-lg bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900 flex items-start gap-2" data-testid="preferences-stats-warning">
            <AlertTriangle className="w-4 h-4 text-rose-700 dark:text-rose-400 shrink-0 mt-0.5" />
            <div className="text-xs text-rose-900 dark:text-rose-200 leading-relaxed">
              <b>Tasa de baja elevada ({data.unsub_pct}%).</b> Considera reducir la frecuencia de envío o segmentar mejor los destinatarios.
              Cada baja por queja (spam) penaliza tu reputación de envío con Gmail/Outlook.
            </div>
          </div>
        )}

        {data.unsub_pct < 1 && data.with_email >= 50 && (
          <div className="mt-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 flex items-start gap-2">
            <Users className="w-4 h-4 text-emerald-700 dark:text-emerald-400 shrink-0 mt-0.5" />
            <div className="text-xs text-emerald-900 dark:text-emerald-200 leading-relaxed">
              <b>Excelente retención.</b> Tus clientes apenas se dan de baja — sigue así.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
